/**
 * @fileoverview Persistent Add to Cart (sections/pdp-persistent-atc.liquid) - DAN-76.
 *
 * Three responsibilities:
 *
 * 1. Reveal. The card is `position: fixed` - top-right on desktop, bottom-centered on mobile.
 *    It stays hidden while the main product section is on screen, is revealed once the shopper
 *    has scrolled past it, and hides again when the footer arrives. Both boundaries are
 *    IntersectionObserver sentinels sharing one root: the strip of viewport the card itself
 *    occupies, read straight off getBoundingClientRect(). That makes the geometry anchor-
 *    agnostic, so no breakpoint branching is needed here, and no scroll handler either.
 *
 *    The start sentinel is the MAIN product section, not this section's own position, so
 *    the reveal point stays correct wherever in the template order the merchant drops it.
 *
 * 2. Variant sync. The card renders no variant inputs of its own - a second set of size
 *    radios would break assets/dansko_sizetabbing.js, which selects them globally. Instead
 *    it listens for `theme:variant:change`, which ProductForm dispatches with
 *    bubbles: true so it reaches document, and updates the price, option values and thumbnail.
 *
 *    The size line follows the PDP's EU/US toggle, reading the unit off
 *    `.size-toggle[data-unit]` and the US labels off the markup dansko_sizetabbing.js builds,
 *    rather than duplicating that script's conversion tables.
 *
 *    Button state is NOT derived here; see 3.
 *
 * 3. Add to Cart delegation. The card has no form. Its button forwards the click to the MAIN
 *    Add to Cart button, and mirrors that button's state (loading / success / disabled /
 *    label) plus its error display back onto the card.
 *
 *    This is what makes the two buttons behave 1:1. The main form's payload includes line item
 *    properties contributed by inputs that live OUTSIDE the form and are bound to it by
 *    `form=` attributes - most importantly properties[Color] from product-siblings.liquid,
 *    which is gated on the `siblings` block's settings. Blocks belong to the `product` section,
 *    so a separate section cannot read them; a hand-rebuilt payload silently drops
 *    properties[Color] and the item lands in the cart as a separate line from the same variant
 *    added via the main button. Delegating runs the real code path instead, which also keeps
 *    analytics and app listeners bound to the main button firing.
 *
 * The JSDoc here is documentation only - the project runs ESLint, not a type checker, so these
 * annotations are not enforced at build time.
 */
(function () {
  'use strict';

  // One <script> tag per section instance, and duplicate tags with the same src each execute.
  if (window.__pdpPersistentAtcInit) {
    return;
  }

  /** @type {boolean} */
  window.__pdpPersistentAtcInit = true;

  // --- Types ---

  /**
   * One image from Shopify's product JSON.
   * @typedef {Object} VariantImage
   * @property {string} src Absolute CDN URL, already carrying a `?v=` cache key.
   * @property {?string} alt
   */

  /**
   * One entry of Shopify's `product.variants` JSON, as emitted by `{{ product | json }}` in
   * snippets/product-buttons.liquid and handed to us on `event.detail.variant`.
   *
   * Only the fields this file reads are listed; the real object carries many more.
   * @typedef {Object} ProductVariant
   * @property {number} id
   * @property {number} price In cents.
   * @property {boolean} available
   * @property {string[]} options Selected option values, ordered by option position.
   * @property {?VariantImage} featured_image Null when the variant has no image of its own.
   */

  /**
   * The strip of viewport the card occupies, expressed as IntersectionObserver rootMargin
   * insets rather than as a rect.
   * @typedef {Object} CardBand
   * @property {number} top Viewport px from the top of the viewport to the card's top edge.
   * @property {number} bottomInset Viewport px from the card's bottom edge to the viewport bottom.
   */

  /**
   * Setters over the reveal state machine. Kept as two independent booleans so the sentinels
   * can fire in any order.
   * @typedef {Object} RevealController
   * @property {function(boolean): void} setPastProduct
   * @property {function(boolean): void} setAtFooter
   */

  // --- Constants ---

  /**
   * `data-pdp-atc-*` entries are this card's own contract with the section Liquid. The `main*`
   * entries are theme-owned selectors in sections/product.liquid and its snippets.
   * @const {Object<string, string>}
   */
  var SELECTORS = {
    card: '[data-pdp-persistent-atc]',
    productSection: '[data-section-type="product"]',
    quickviewModal: '[data-quickview-modal]',
    // The footer is rendered through {% sections 'group-footer' %}, so its wrapper id is
    // shopify-section-sections--<theme-id>__footer, not shopify-section-footer. Matches
    // subfooter too; querySelector takes the first, which is the footer proper.
    footer: '[data-section-type="footer"]',
    image: '[data-pdp-atc-image]',
    price: '[data-pdp-atc-price]',
    option: '[data-pdp-atc-option]',
    button: '[data-pdp-atc-button]',
    buttonText: '[data-pdp-atc-button-text]',
    errors: '[data-pdp-atc-errors]',
    edit: '[data-pdp-atc-edit]',
    // Unscoped on purpose; any instance will do. See formatMoney().
    productForm: 'product-form',
    // Built at runtime by assets/dansko_sizetabbing.js.
    sizeToggle: '.size-toggle',
    usSizeInput: '.not-eu input[type="radio"]',
    radioButton: '.radio__button',
    stickyHeader: '[data-header-sticky]',
    mainButtonsWrapper: '[data-buttons-wrapper]',
    mainAddButton: '[data-add-to-cart]',
    mainButtonText: '[data-add-to-cart-text]',
    mainErrors: '[data-error-display]',
    variantSelectors: '.product__block__variants',
    sizeFieldset: '.selector-wrapper[data-select-label="Size"] input[type="radio"]',
    firstFocusable: 'input, select, button, [href]'
  };

  /**
   * `headerRevealed` is theme-owned, set by the Sticky class in theme.js. The rest are ours.
   * @const {Object<string, string>}
   */
  var CLASSES = {
    visible: 'is-visible',
    headerOffset: 'pdp-persistent-atc--header-offset',
    headerRevealed: 'js__header__stuck--trigger-animation'
  };

  /** @const {string} Marks a card as already wired, so re-entry through init() is a no-op. */
  var INIT_ATTR = 'data-pdp-atc-initialized';

  // --- Environment ---

  /**
   * @returns {boolean} True when the visitor has asked for reduced motion.
   */
  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * @returns {boolean} True inside the Shopify theme editor, where the card is shown outright
   *   so it can be seen and configured.
   */
  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /**
   * The strip of viewport the card occupies, as IntersectionObserver rootMargin insets.
   *
   * Because the card is position: fixed, its bounding rect IS that strip - so the same two
   * numbers describe the desktop top-right card and the mobile bottom-centered one, and this
   * file needs no breakpoint branching. It also makes the resolved CSS the single source of
   * truth: nothing here re-derives --menu-height-sticky or the offset settings, so there is
   * no arithmetic to drift out of step with the stylesheet.
   *
   * getBoundingClientRect is accurate under visibility: hidden. Under display: none (the
   * device-targeting classes) it returns zeros, which collapses the root so the card simply
   * never reveals - correct, since it is hidden at that breakpoint anyway.
   *
   * @param {HTMLElement} card The card root, `[data-pdp-persistent-atc]`.
   * @returns {CardBand}
   */
  function cardBand(card) {
    var rect = card.getBoundingClientRect();

    return {
      top: rect.top,
      bottomInset: Math.max(0, window.innerHeight - rect.bottom)
    };
  }

  /**
   * Formats cents by calling the theme's own money formatter rather than reimplementing it, so
   * the card's price cannot drift from the PDP's - the currency-code suffix rule stays in one
   * place.
   *
   * There is no free-standing global to call: `Shopify.formatMoney` comes from
   * shopify_common.js, which layout/theme.liquid loads only for customer pages, and
   * `themeVendor.themeCurrency.formatMoney` is only the primitive, without the suffix rule.
   * What IS reachable is `formattingMoney` (theme.dev.js:9595), a prototype method of
   * `class ProductForm extends HTMLElement` registered as <product-form>.
   *
   * Any <product-form> will do, so this is deliberately NOT scoped to the main product section
   * or away from quick views: the method reads only the `theme` globals and touches no instance
   * state, so every element returns an identical result.
   *
   * @param {number} cents
   * @returns {?string} Formatted money, or null when no <product-form> is on the page or the
   *   theme globals are missing - callers must leave the existing markup alone in that case.
   */
  function formatMoney(cents) {
    var form = document.querySelector(SELECTORS.productForm);

    if (!form || typeof form.formattingMoney !== 'function' || !window.theme) {
      return null;
    }

    return form.formattingMoney(cents);
  }

  // --- Variant sync ---

  /**
   * @param {HTMLElement} card
   * @param {?ProductVariant} variant Null for an unavailable option combination.
   * @returns {void}
   */
  function updatePrice(card, variant) {
    var priceEl = card.querySelector(SELECTORS.price);

    if (!priceEl || !variant) {
      return;
    }

    var formatted = formatMoney(variant.price);

    if (formatted !== null) {
      priceEl.innerHTML = formatted;
    }
  }

  /**
   * Which unit the PDP's size selector is currently showing.
   *
   * `.size-toggle[data-unit]` is a purpose-built state flag that dansko_sizetabbing.js keeps
   * current through syncSizeToggle() (dansko_sizetabbing.js:144), so this is a single read
   * rather than an inference from the selector's markup.
   *
   * @returns {?string} 'eu', 'us', or null when the EU/US overlay is absent - a non-shoe
   *   product, or a product whose Size option renders as a dropdown so the script bails.
   */
  function sizeUnit() {
    var toggle = document.querySelector(SELECTORS.sizeToggle);
    var unit = toggle && toggle.getAttribute('data-unit');

    return unit === 'eu' || unit === 'us' ? unit : null;
  }

  /**
   * The US label(s) dansko_sizetabbing.js generated for one EU size, read back off its own
   * markup rather than duplicating its conversion tables.
   *
   * Usually one. It is TWO for EU 41, 42 and 43 on a product carrying both ranges, because the
   * womens table covers 35-43 and the mens table covers 41-48 (dansko_sizetabbing.js:47, :73).
   * Those pairs are four sizes apart, so neither can be dropped.
   *
   * @param {string} euValue The variant's real Size option value.
   * @returns {Array<{scope: string, label: string}>} In DOM order, so womens precedes mens.
   */
  function usSizeRanges(euValue) {
    var out = [];
    var inputs = document.querySelectorAll(SELECTORS.usSizeInput);

    Array.prototype.forEach.call(inputs, function (/** @type {HTMLInputElement} */ input) {
      if (input.value !== euValue) {
        return;
      }

      // The <label> is a sibling, not a wrapper, so the input has no labels collection.
      var wrapper = input.closest(SELECTORS.radioButton);
      var label = wrapper && wrapper.querySelector('label');
      var text = label ? label.textContent.trim() : '';

      if (!text) {
        return;
      }

      out.push({scope: input.getAttribute('name') === 'altSizeM' ? 'M' : 'W', label: text});
    });

    return out;
  }

  /**
   * Renders the size line to match whichever unit the PDP is showing.
   *
   * There is nothing to mirror for this: dansko_sizetabbing.js:38 hides the size legend, so the
   * PDP never renders selected-size text at all, only a highlighted tile. The card's size line
   * is additive UI from the Figma design ("36 EU"), so it has to be constructed.
   *
   * @param {HTMLElement} option The size line, whose data-pdp-atc-option-size holds the scale
   *   Liquid rendered with and so acts as the fallback unit.
   * @param {string} euValue
   * @returns {string}
   */
  function sizeLabel(option, euValue) {
    var unit = sizeUnit();

    // No overlay: fall back to the scale Liquid rendered with, so the unit is not dropped.
    if (unit === null && option.getAttribute('data-pdp-atc-option-size') === 'eu') {
      unit = 'eu';
    }

    if (unit === null) {
      return euValue;
    }

    if (unit === 'eu') {
      return euValue + ' EU';
    }

    var ranges = usSizeRanges(euValue);

    // Outside both tables there is no tile in the US view either, so state EU rather than
    // invent a conversion.
    if (!ranges.length) {
      return euValue + ' EU';
    }

    if (ranges.length === 1) {
      return ranges[0].label + ' US';
    }

    return (
      ranges
        .map(function (range) {
          return range.scope + ' ' + range.label;
        })
        .join(' / ') + ' US'
    );
  }

  /**
   * Renders one summary line from its stored raw value. Split out from updateOptions so the unit
   * toggle can re-render without a variant event to read.
   *
   * @param {HTMLElement} option
   * @returns {void}
   */
  function renderOption(option) {
    var raw = option.getAttribute('data-pdp-atc-option-value');

    if (raw === null) {
      return;
    }

    var text = option.hasAttribute('data-pdp-atc-option-size') ? sizeLabel(option, raw) : raw;

    if (option.textContent !== text) {
      option.textContent = text;
    }
  }

  /**
   * Re-renders every summary line from what is already stored. Used by the unit toggle.
   *
   * @param {HTMLElement} card
   * @returns {void}
   */
  function renderOptions(card) {
    Array.prototype.forEach.call(card.querySelectorAll(SELECTORS.option), renderOption);
  }

  /**
   * Stores each summary line's raw value from `variant.options`, keyed by the option index
   * Liquid stamped on it, then renders. The width line carries no index attribute and is
   * deliberately skipped: width comes from the shoe-fit metaobject and is fixed for the product.
   *
   * @param {HTMLElement} card
   * @param {?ProductVariant} variant
   * @returns {void}
   */
  function updateOptions(card, variant) {
    if (!variant || !variant.options) {
      return;
    }

    var options = card.querySelectorAll(SELECTORS.option);

    Array.prototype.forEach.call(options, function (/** @type {HTMLElement} */ option) {
      var index = parseInt(option.getAttribute('data-pdp-atc-option-index'), 10);

      if (isNaN(index)) {
        return;
      }

      var value = variant.options[index];

      if (typeof value === 'undefined' || value === null) {
        return;
      }

      // Stored raw and unit-agnostic, so a later unit toggle can re-render from it.
      option.setAttribute('data-pdp-atc-option-value', value);
      renderOption(option);
    });
  }

  /**
   * @param {HTMLElement} card
   * @param {?ProductVariant} variant
   * @returns {void}
   */
  function updateImage(card, variant) {
    var image = card.querySelector(SELECTORS.image);

    if (!image) {
      return;
    }

    /** @type {?VariantImage} */
    var featured = variant && variant.featured_image;

    // Without this, a variant that has no image of its own keeps the previous thumbnail.
    if (!featured || !featured.src) {
      var fallback = image.getAttribute('data-pdp-atc-fallback-src');

      if (fallback && image.getAttribute('src') !== fallback) {
        image.setAttribute('src', fallback);
        image.setAttribute('alt', image.getAttribute('data-pdp-atc-fallback-alt') || '');
      }

      return;
    }

    // featured_image.src already carries a ?v= cache key, so width is appended.
    var sized =
      featured.src.indexOf('?') === -1 ? featured.src + '?width=200' : featured.src + '&width=200';

    if (image.getAttribute('src') !== sized) {
      image.setAttribute('src', sized);
    }

    if (featured.alt) {
      image.setAttribute('alt', featured.alt);
    }
  }

  /**
   * theme:variant:change bubbles to document from EVERY <product-form> on the page, and a PDP
   * has more than the main one. Opening a quick view - from Related Products, or from the
   * PDP's own Buy It With upsell - injects a <product-form> for a DIFFERENT product, which
   * fires this event on connect and on every variant change inside the modal. Acting on those
   * would repaint this card with the wrong product's price, options and thumbnail.
   *
   * The upsell quick view lives inside the main product section, so a section check alone is
   * not enough; the [data-quickview-modal] ancestor is what actually distinguishes them.
   *
   * @param {?EventTarget} target The event's target, i.e. the dispatching <product-form>.
   * @returns {boolean} True only for the main PDP form.
   */
  function isMainProductForm(target) {
    if (!target || typeof target.closest !== 'function') {
      return false;
    }

    if (target.closest(SELECTORS.quickviewModal)) {
      return false;
    }

    return Boolean(target.closest(SELECTORS.productSection));
  }

  /**
   * @param {HTMLElement} card
   * @param {?ProductVariant} variant
   * @returns {void}
   */
  function syncVariant(card, variant) {
    updatePrice(card, variant);
    updateOptions(card, variant);
    updateImage(card, variant);
  }

  // --- Add to Cart delegation ---

  /**
   * The main PDP add-to-cart cluster. Scoped to the product section and away from any quick
   * view so this can never resolve to an upsell or related-product button.
   *
   * @returns {?HTMLElement} `[data-buttons-wrapper]`, or null when the template has no
   *   add-buttons block, the product is coming soon, or the gift-card cart rule replaced it.
   */
  function mainButtonsWrapper() {
    var section = document.querySelector(SELECTORS.productSection);

    if (!section) {
      return null;
    }

    var wrappers = section.querySelectorAll(SELECTORS.mainButtonsWrapper);

    for (var i = 0; i < wrappers.length; i++) {
      if (!wrappers[i].closest(SELECTORS.quickviewModal)) {
        return wrappers[i];
      }
    }

    return null;
  }

  /**
   * Resolved fresh on every call rather than cached, so a click still lands on the right node
   * if the main cluster is re-rendered.
   *
   * @returns {?HTMLButtonElement} The real PDP Add to Cart button, or null when absent.
   */
  function mainAddButton() {
    var wrapper = mainButtonsWrapper();

    return wrapper ? wrapper.querySelector(SELECTORS.mainAddButton) : null;
  }

  /**
   * Copy the main button's state onto the card's button. `loading` and `has-success` drive the
   * shared .btn-state-* CSS; `disabled` and the label cover sold out, unavailable and
   * pre-order without this file having to re-derive any of them.
   *
   * @param {HTMLElement} card
   * @returns {void}
   */
  function mirrorButtonState(card) {
    var button = card.querySelector(SELECTORS.button);
    var source = mainAddButton();

    if (!button || !source) {
      return;
    }

    button.classList.toggle('loading', source.classList.contains('loading'));
    button.classList.toggle('has-success', source.classList.contains('has-success'));
    button.disabled = source.disabled;

    if (source.classList.contains('loading')) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }

    var label = button.querySelector(SELECTORS.buttonText);
    var sourceLabel = source.querySelector(SELECTORS.mainButtonText);

    if (label && sourceLabel) {
      // textContent, not innerHTML: the main label sits alongside a bullet and price span
      // that the card's compact button omits.
      var text = sourceLabel.textContent.trim();

      if (text && label.textContent.trim() !== text) {
        label.textContent = text;
      }
    }
  }

  /**
   * Mirror the main form's error display. It is inside the product section and therefore off
   * screen whenever the card is visible, so a failed add would otherwise be silent here.
   * The theme slides the node up after a timeout without clearing its markup, so display is
   * checked as well as content.
   *
   * @param {HTMLElement} card
   * @returns {void}
   */
  function mirrorErrors(card) {
    var target = card.querySelector(SELECTORS.errors);
    var wrapper = mainButtonsWrapper();
    var source = wrapper ? wrapper.querySelector(SELECTORS.mainErrors) : null;

    if (!target || !source) {
      return;
    }

    var visible =
      source.innerHTML.trim() !== '' && window.getComputedStyle(source).display !== 'none';
    var markup = visible ? source.innerHTML : '';

    if (target.innerHTML !== markup) {
      target.innerHTML = markup;
    }
  }

  /**
   * Wires the card's button to the main one and starts the state mirrors.
   *
   * @param {HTMLElement} card
   * @returns {boolean} False when there is nothing to delegate to, which the caller treats as
   *   "never reveal this card" so it cannot present a dead control.
   */
  function delegateAddToCart(card) {
    var button = card.querySelector(SELECTORS.button);

    if (!button) {
      return false;
    }

    var source = mainAddButton();

    // No main button means no add-buttons block, so there is nothing to delegate to.
    if (!source) {
      return false;
    }

    button.addEventListener('click', function (/** @type {MouseEvent} */ event) {
      event.preventDefault();

      var target = mainAddButton();

      if (target && !target.disabled) {
        target.click();
      }
    });

    if (typeof window.MutationObserver === 'function') {
      var wrapper = mainButtonsWrapper();

      // Subtree because the label lives in a child span, while `loading` / `has-success` /
      // `disabled` land on the button itself.
      new MutationObserver(function () {
        mirrorButtonState(card);
      }).observe(source, {
        attributes: true,
        attributeFilter: ['class', 'disabled'],
        childList: true,
        subtree: true,
        characterData: true
      });

      var errorSource = wrapper ? wrapper.querySelector(SELECTORS.mainErrors) : null;

      if (errorSource) {
        new MutationObserver(function () {
          mirrorErrors(card);
        }).observe(errorSource, {
          attributes: true,
          attributeFilter: ['style', 'class'],
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    }

    mirrorButtonState(card);

    return true;
  }

  // --- Edit link ---

  /**
   * offsetParent is null for anything display:none - the EU size fieldset is hidden while
   * the US unit is active, so an unconditional focus() there would silently do nothing.
   *
   * Not valid for position: fixed elements, which report a null offsetParent even when
   * visible. Only ever called on controls inside the product section.
   *
   * @param {?Element} el
   * @returns {boolean}
   */
  function isVisible(el) {
    return Boolean(el && el.offsetParent !== null);
  }

  /**
   * Scrolls the main variant selectors into view and moves focus there. Bound to the card's
   * Edit link.
   *
   * @returns {void}
   */
  function scrollToSelectors() {
    var productSection = document.querySelector(SELECTORS.productSection);

    if (!productSection) {
      return;
    }

    var target = productSection.querySelector(SELECTORS.variantSelectors);

    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start'
    });

    // Size input if there is one, else the first visible control, so keyboard users land
    // where the card sent them.
    /** @type {?Element} */
    var focusTarget = productSection.querySelector(SELECTORS.sizeFieldset);

    if (!isVisible(focusTarget)) {
      focusTarget = null;

      var candidates = target.querySelectorAll(SELECTORS.firstFocusable);

      for (var i = 0; i < candidates.length; i++) {
        if (isVisible(candidates[i])) {
          focusTarget = candidates[i];
          break;
        }
      }
    }

    if (focusTarget) {
      focusTarget.focus({preventScroll: true});
    }

    // Not hidden here on purpose: scrolling up brings the product section back into view,
    // so the observer hides it and its state stays authoritative.
  }

  // --- Reveal ---

  /**
   * @param {HTMLElement} card
   * @returns {void}
   */
  function show(card) {
    card.classList.add(CLASSES.visible);
    card.setAttribute('aria-hidden', 'false');
  }

  /**
   * @param {HTMLElement} card
   * @returns {void}
   */
  function hide(card) {
    card.classList.remove(CLASSES.visible);
    card.setAttribute('aria-hidden', 'true');
  }

  /**
   * Keeps the card clear of the sticky header while that header is on screen.
   *
   * Dansko runs the header in `directional` mode, which reveals it on scroll up only. In that
   * mode theme.js leaves --menu-height-sticky at 0 (it is set from --menu-height only for
   * `data-header-sticky="sticky"`), so the card's resting top offset does not account for the
   * header, and the revealed header - z-index 6000 against the card's 5000 - draws over it.
   *
   * The header's own class is mirrored rather than re-deriving scroll direction, so the card
   * cannot disagree with the header about whether it is showing. Only the desktop rule reads
   * the offset; the mobile card is bottom-anchored and can never be overlapped.
   *
   * @param {HTMLElement} card
   * @param {?function(): void} onShift Called once the card has finished moving, so the reveal
   *   sentinels can be rebuilt against its new position. The band is captured as numbers, so
   *   without this they would keep describing the old resting offset.
   * @returns {void}
   */
  function observeStickyHeader(card, onShift) {
    var header = document.querySelector(SELECTORS.stickyHeader);

    if (!header) {
      return;
    }

    /** @returns {void} */
    function apply() {
      card.classList.toggle(
        CLASSES.headerOffset,
        header.classList.contains(CLASSES.headerRevealed)
      );
    }

    new MutationObserver(apply).observe(header, {
      attributes: true,
      attributeFilter: ['class']
    });

    if (typeof onShift === 'function') {
      // transitionend rather than the class change: the card is still travelling at that point,
      // so measuring then would capture a mid-transition rect.
      card.addEventListener('transitionend', function (/** @type {TransitionEvent} */ event) {
        if (event.propertyName === 'top') {
          onShift();
        }
      });
    }

    apply();
  }

  /**
   * State is driven by two independent booleans so either observer can fire in any order:
   * the card shows only once the product section is behind us AND the footer is not yet here.
   *
   * @param {HTMLElement} card
   * @returns {RevealController}
   */
  function createRevealController(card) {
    /** @type {{pastProduct: boolean, atFooter: boolean}} */
    var state = {pastProduct: false, atFooter: false};

    /** @returns {void} */
    function apply() {
      if (state.pastProduct && !state.atFooter) {
        show(card);
      } else {
        hide(card);
      }
    }

    return {
      /**
       * @param {boolean} value True once the product section is entirely above the card.
       * @returns {void}
       */
      setPastProduct: function (value) {
        state.pastProduct = value;
        apply();
      },
      /**
       * @param {boolean} value True once the footer overlaps the card.
       * @returns {void}
       */
      setAtFooter: function (value) {
        state.atFooter = value;
        apply();
      }
    };
  }

  /**
   * Starts the two reveal sentinels. Silently does nothing when the main product section is
   * absent or IntersectionObserver is unsupported, leaving the card permanently hidden.
   *
   * @param {HTMLElement} card
   * @returns {?function(): void} The rebuild hook, for callers that move the card. Null when
   *   no sentinels were started.
   */
  function observeReveal(card) {
    var productSection = document.querySelector(SELECTORS.productSection);

    if (!productSection || typeof window.IntersectionObserver !== 'function') {
      return null;
    }

    /** @type {RevealController} */
    var controller = createRevealController(card);

    /** @type {IntersectionObserver[]} */
    var observers = [];

    /**
     * (Re)creates both sentinels against the card's current band. Safe to call repeatedly:
     * existing observers are disconnected first.
     * @returns {void}
     */
    function build() {
      observers.forEach(function (/** @type {IntersectionObserver} */ observer) {
        observer.disconnect();
      });
      observers = [];

      // Both sentinels share one root, the card's own strip, so desktop and mobile need no
      // branching here.
      var band = cardBand(card);
      var rootMargin = -band.top + 'px 0px -' + band.bottomInset + 'px 0px';

      var productObserver = new IntersectionObserver(
        function (/** @type {IntersectionObserverEntry[]} */ entries) {
          entries.forEach(function (/** @type {IntersectionObserverEntry} */ entry) {
            // boundingClientRect is in viewport coordinates, so compare against band.top, not
            // 0. The bottom check separates "scrolled past" from "not reached yet"; without it
            // the card flashes on load.
            controller.setPastProduct(
              !entry.isIntersecting && entry.boundingClientRect.bottom <= band.top
            );
          });
        },
        {rootMargin: rootMargin, threshold: 0}
      );
      productObserver.observe(productSection);
      observers.push(productObserver);

      var footer = document.querySelector(SELECTORS.footer);

      if (footer) {
        // The shrunken root makes the footer count only once it overlaps the card.
        var footerObserver = new IntersectionObserver(
          function (/** @type {IntersectionObserverEntry[]} */ entries) {
            entries.forEach(function (/** @type {IntersectionObserverEntry} */ entry) {
              controller.setAtFooter(entry.isIntersecting);
            });
          },
          {rootMargin: rootMargin, threshold: 0}
        );
        footerObserver.observe(footer);
        observers.push(footerObserver);
      }
    }

    build();

    // --menu-height-sticky may not be set by theme.js yet when this deferred script runs, and
    // the band is captured as numbers, so rebuild once the page has settled.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', build, {once: true});
    }

    // Resize moves the band, and crossing 767px moves the card to the opposite edge outright.
    document.addEventListener('theme:resize', build);

    // The card's own height is no longer fixed: the option lines wrap, so a longer colourway or the
    // dual-gender US size string ("W 10.5-11 / M 7.5-8 US") adds a line and grows the card without
    // any viewport resize. The band is captured as numbers at build time, so without this the
    // reveal and footer boundaries stay off by the height delta until the next resize. Watching the
    // card is cheaper and more direct than hooking every code path that can change its text, and
    // build() cannot change the card's size, so this does not re-trigger itself.
    if (typeof window.ResizeObserver === 'function') {
      new window.ResizeObserver(build).observe(card);
    }

    return build;
  }

  // --- Init ---

  /**
   * Wires one card: variant sync, the Edit link, add-to-cart delegation, and the reveal.
   * Idempotent via INIT_ATTR.
   *
   * @param {HTMLElement} card `[data-pdp-persistent-atc]`.
   * @returns {void}
   */
  function initCard(card) {
    if (card.getAttribute(INIT_ATTR) === 'true') {
      return;
    }
    card.setAttribute(INIT_ATTR, 'true');

    document.addEventListener('theme:variant:change', function (
      /** @type {CustomEvent<{variant: ?ProductVariant}>} */ event
    ) {
      if (!event.detail || !isMainProductForm(event.target)) {
        return;
      }
      syncVariant(card, event.detail.variant);
    });

    // Flipping the unit changes no variant, so it fires no theme:variant:change. Delegated
    // because dansko_sizetabbing.js builds .size-toggle at runtime; bubbling also guarantees
    // that script has already written data-unit by the time this runs.
    document.addEventListener('click', function (/** @type {MouseEvent} */ event) {
      var target = event.target;

      if (target && typeof target.closest === 'function' && target.closest(SELECTORS.sizeToggle)) {
        renderOptions(card);
      }
    });

    var edit = card.querySelector(SELECTORS.edit);

    if (edit) {
      edit.addEventListener('click', scrollToSelectors);
    }

    var canAddToCart = delegateAddToCart(card);

    // Shown outright in the editor so it can be configured, even with nothing to delegate to.
    if (isDesignMode()) {
      observeStickyHeader(card, null);
      show(card);
      return;
    }

    if (!canAddToCart) {
      return;
    }

    observeStickyHeader(card, observeReveal(card));
  }

  /**
   * Finds and wires every card within a scope.
   *
   * @param {?(Document|Element)} root Search root; falls back to `document`. On
   *   `shopify:section:load` this is the reloaded section element.
   * @returns {void}
   */
  function init(root) {
    var scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    var cards = scope.querySelectorAll(SELECTORS.card);

    Array.prototype.forEach.call(cards, initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: wire sections added or reloaded in the customizer.
  document.addEventListener('shopify:section:load', function (/** @type {CustomEvent} */ event) {
    init(event.target || document);
  });
})();
